<template>
    <div class="pdf-to-ppt-container">
        <!-- Upload Section -->
        <div v-if="!file" 
             class="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center hover:border-blue-400 transition-colors cursor-pointer bg-gray-50"
             @dragover.prevent
             @drop.prevent="handleDrop"
             @click="$refs.fileInput.click()">
            <input type="file" ref="fileInput" class="hidden" accept=".pdf" @change="handleFileSelect">
            
            <div class="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
            </div>
            <h3 class="text-lg font-semibold text-gray-800 mb-2">Click or Drag PDF to Convert</h3>
            <p class="text-gray-500 text-sm">Convert your PDF files to PowerPoint slides easily.</p>
        </div>

        <!-- Processing Section -->
        <div v-else class="bg-white border border-gray-200 rounded-xl p-6">
            <div class="flex items-center justify-between mb-6">
                <div class="flex items-center space-x-4">
                    <div class="w-12 h-12 bg-red-100 text-red-600 rounded flex items-center justify-center">
                        <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M7 2H17L21 6V18C21 19.1 20.1 20 19 20H7C5.89 20 5 19.1 5 18V4C5 2.9 5.89 2 7 2M13 9H18.5L13 3.5V9M7 4V18H19V11H11V4H7Z" />
                        </svg>
                    </div>
                    <div>
                        <h4 class="font-semibold text-gray-800">{{ file.name }}</h4>
                        <p class="text-xs text-gray-500">{{ formatSize(file.size) }}</p>
                    </div>
                </div>
                <button @click="reset" class="text-gray-400 hover:text-red-500 transition-colors">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            <div v-if="!isConverting && !downloadUrl" class="text-center">
                <button @click="convertPdf" class="w-full bg-blue-600 text-white font-semibold py-3 rounded-lg hover:bg-blue-700 transition-colors">
                    Convert to PPT
                </button>
            </div>

            <div v-if="isConverting" class="text-center py-4">
                <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p class="text-gray-600 font-medium">Converting PDF to PowerPoint slides...</p>
                <p class="text-xs text-gray-400 mt-1">This may take a few moments</p>
            </div>

            <div v-if="downloadUrl" class="text-center space-y-4">
                <div class="p-4 bg-green-50 text-green-700 rounded-lg text-sm font-medium">
                    Conversion Successful! Your PowerPoint file is ready.
                </div>
                <a :href="downloadUrl" :download="pptName" class="block w-full bg-green-600 text-white font-semibold py-3 rounded-lg hover:bg-green-700 transition-colors text-center">
                    Download PowerPoint
                </a>
                <button @click="reset" class="text-sm text-blue-600 hover:underline">
                    Convert another file
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';

const file = ref(null);
const isConverting = ref(false);
const downloadUrl = ref(null);
const pptName = ref('');

const props = defineProps(['translations', 'currentLang']);

const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
        file.value = selectedFile;
    }
};

const handleDrop = (event) => {
    const droppedFile = event.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/pdf') {
        file.value = droppedFile;
    }
};

const convertPdf = () => {
    isConverting.value = true;
    
    // Simulate conversion for demonstration
    setTimeout(() => {
        isConverting.value = false;
        // In a real app, this would be an API response or client-side processing result
        downloadUrl.value = '#'; 
        pptName.value = file.value.name.replace('.pdf', '') + '.pptx';
    }, 3000);
};

const formatSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const reset = () => {
    file.value = null;
    isConverting.value = false;
    downloadUrl.value = null;
    pptName.value = '';
};
</script>

<style scoped>
.pdf-to-ppt-container {
    max-width: 600px;
    margin: 0 auto;
}
</style>
